import { Phone, ArrowRight, Check, Shield, Heart, Users, Clock, Stethoscope, Pill, Syringe } from 'lucide-react';
import { Link } from 'react-router-dom';

const HERO_FULLWIDTH = "https://customer-assets.emergentagent.com/job_087dcaaa-f596-41d4-9f4c-171b9ddad075/artifacts/rb9ahvuh_DFR_8577.jpg";
const DOCTOR_ABOUT = "/dr_eduardo_haras.jpg";
const WHATSAPP_LINK = "https://wa.me/5541988451399?text=Olá,%20gostaria%20de%20agendar%20uma%20avaliação%20médica.";

export const HomePage = () => {
  return (
    <main data-testid="home-page">
      {/* Hero Section - Full Width Image */}
      <section data-testid="hero-section" className="relative min-h-screen">
        {/* Full Width Background Image */}
        <div className="absolute inset-0">
          <img 
            src={HERO_FULLWIDTH}
            alt="Dr. Eduardo Hadas - Consultório"
            className="w-full h-full object-cover"
          />
          {/* Dark overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
        </div>
        
        {/* Content over image */}
        <div className="container-custom relative z-10 min-h-screen flex items-center">
          <div className="max-w-2xl py-32">
            <p className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] mb-6 font-medium">
              Dr. Eduardo Hadas — CRM-PR 40929
            </p>
            
            <h1 
              className="text-4xl md:text-5xl lg:text-6xl leading-[1.1] mb-8 text-white"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Emagrecimento e hormônios não são{' '}
              <span className="text-[#D4AF37]">atalhos</span>.
            </h1>
            
            <p className="text-lg md:text-xl text-white/90 leading-relaxed mb-6">
              São processos médicos que exigem critério e acompanhamento real.
            </p>
            
            <p className="text-base text-white/70 leading-relaxed mb-10 max-w-xl">
              Tratamentos individualizados em emagrecimento clínico e reposição hormonal, 
              conduzidos com ciência, segurança e presença médica contínua.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-button"
                className="btn-primary flex items-center justify-center gap-2"
              >
                <Phone className="w-5 h-5" />
                Agendar avaliação médica
              </a>
              <Link
                to="/como-funciona"
                data-testid="hero-secondary-button"
                className="btn-secondary flex items-center justify-center gap-2 !bg-white/10 !border-white/30 !text-white hover:!bg-[#D4AF37] hover:!border-[#D4AF37]"
              >
                Entenda como funciona
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-[#D4AF37] rounded-full mt-2 animate-bounce" />
          </div>
        </div>
      </section>

      {/* Block 2 - Seu corpo não é um experimento */}
      <section data-testid="experiment-section" className="section-padding relative">
        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-16 h-1 bg-gradient-to-r from-[#C9A227] to-[#D4AF37] mx-auto mb-8 rounded-full" />
            
            <h2 
              className="section-title mb-8"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Seu corpo não é um experimento.
            </h2>
            
            <p className="text-lg text-[#555555] leading-relaxed mb-6">
              Se você já tentou dietas, remédios, treinos, protocolos prontos ou tratamentos 
              hormonais sem resultado sustentável, você não está sozinho.
            </p>
            
            <p className="text-lg text-[#555555] leading-relaxed mb-6">
              A maioria das pessoas falha não por falta de esforço — mas por falta de 
              acompanhamento médico real, individualização e critério científico.
            </p>
            
            <p className="text-lg text-[#2A2A2A] leading-relaxed font-medium">
              Emagrecimento e equilíbrio hormonal não são eventos pontuais.
              <br />
              <span className="text-[#C9A227]">São processos médicos</span> que exigem avaliação, ajustes contínuos e responsabilidade.
            </p>
          </div>
        </div>
      </section>

      {/* Block 3 - Para quem é */}
      <section data-testid="for-who-section" className="section-padding bg-[#E8DCC8]/50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 
              className="section-title"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Este acompanhamento é para você?
            </h2>
          </div>
          
          <div className="max-w-2xl mx-auto">
            {/* Para quem é */}
            <div className="card-premium">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C9A227] flex items-center justify-center shadow-lg">
                  <Check className="w-6 h-6 text-white" />
                </div>
                <h3 
                  className="text-2xl text-[#2A2A2A]"
                  style={{ fontFamily: 'var(--font-heading)' }}
                >
                  Para quem é
                </h3>
              </div>
              
              <ul className="space-y-4">
                {[
                  'Para quem já tentou de tudo e quer um tratamento sério',
                  'Para quem busca emagrecimento sustentável, não atalhos',
                  'Para quem quer equilibrar hormônios com segurança',
                  'Para quem valoriza presença médica e acompanhamento contínuo',
                  'Para quem quer resultado com critério, não com excesso',
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#C9A227] flex-shrink-0 mt-0.5" />
                    <span className="text-[#555555]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Block 4 - Quem é o Dr. Edu Hadas */}
      <section data-testid="about-preview-section" className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-5 gap-12 lg:gap-16 items-center">
            {/* Image - 2 columns */}
            <div className="lg:col-span-2 relative">
              <div className="aspect-[3/4] rounded-lg overflow-hidden shadow-2xl">
                <img 
                  src={DOCTOR_ABOUT}
                  alt="Dr. Eduardo Hadas"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-gradient-to-br from-[#D4AF37]/20 to-[#C9A227]/10 rounded-full -z-10" />
            </div>
            
            {/* Content - 3 columns */}
            <div className="lg:col-span-3">
              <p className="text-sm uppercase tracking-[0.2em] text-[#C9A227] mb-4 font-medium">
                Conheça o médico
              </p>
              
              <h2 
                className="section-title mb-6"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                Antes de ser médico,<br />eu fui paciente.
              </h2>
              
              <p className="text-lg text-[#555555] leading-relaxed mb-6">
                Eu convivi com obesidade desde a infância, vivi o efeito sanfona e sofri 
                na pele os riscos de um tratamento hormonal mal conduzido.
              </p>
              
              <p className="text-lg text-[#555555] leading-relaxed mb-6">
                Isso mudou minha vida — e mudou a forma como eu pratico medicina hoje.
              </p>
              
              <p className="text-lg text-[#555555] leading-relaxed mb-8">
                Ao invés de aceitar a situação, mergulhei nos estudos em emagrecimento, 
                reposição hormonal e terapias endovenosas.
              </p>
              
              <Link
                to="/sobre"
                data-testid="about-preview-cta"
                className="btn-secondary inline-flex items-center gap-2"
              >
                Conheça minha história completa
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Block 5 - Como Funciona o Acompanhamento */}
      <section data-testid="methodology-preview-section" className="section-padding bg-[#D9CFC0]/30">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 
              className="section-title mb-4"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Aqui você não recebe apenas uma prescrição.
            </h2>
            <p className="text-xl text-[#C9A227]" style={{ fontFamily: 'var(--font-heading)' }}>
              Você recebe acompanhamento médico real.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                number: '01',
                title: 'Consulta e anamnese aprofundada',
                description: 'Entendemos sintomas, histórico, queixas físicas, emocionais e funcionamento hormonal.'
              },
              {
                number: '02',
                title: 'Avaliação laboratorial completa',
                description: 'Solicitamos exames para definir valores basais e guiar todas as decisões.'
              },
              {
                number: '03',
                title: 'Estratégia terapêutica individualizada',
                description: 'Reposição hormonal, reativação do eixo e/ou emagrecimento clínico conforme cada caso.'
              },
              {
                number: '04',
                title: 'Acompanhamento contínuo',
                description: 'Reavaliações periódicas, correções de dose e prevenção de efeitos colaterais.'
              },
            ].map((step, index) => (
              <div key={index} className="card-premium">
                <span 
                  className="text-4xl font-light mb-4 block bg-gradient-to-r from-[#D4AF37] to-[#C9A227] bg-clip-text text-transparent"
                  style={{ fontFamily: 'var(--font-heading)' }}
                >
                  {step.number}
                </span>
                <h3 
                  className="text-xl text-[#2A2A2A] mb-3"
                  style={{ fontFamily: 'var(--font-heading)' }}
                >
                  {step.title}
                </h3>
                <p className="text-[#555555] text-sm leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link
              to="/como-funciona"
              data-testid="methodology-preview-cta"
              className="btn-secondary inline-flex items-center gap-2"
            >
              Entenda como funciona o acompanhamento
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Block 6 - Serviços Principais */}
      <section data-testid="services-section" className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-[0.2em] text-[#C9A227] mb-4 font-medium">
              Especialidades
            </p>
            <h2 
              className="section-title"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Áreas de atuação
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Emagrecimento Clínico */}
            <div className="service-card group">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C9A227] flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform">
                <Stethoscope className="w-8 h-8 text-white" />
              </div>
              <h3 
                className="text-2xl text-[#2A2A2A] mb-4"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                Emagrecimento Clínico
              </h3>
              <p className="text-[#555555] mb-6 leading-relaxed">
                Tratamento médico individualizado para emagrecimento sustentável, 
                com acompanhamento contínuo.
              </p>
              <Link
                to="/emagrecimento"
                data-testid="service-emagrecimento-link"
                className="text-[#C9A227] font-semibold inline-flex items-center gap-2 group-hover:gap-3 transition-all"
              >
                Saiba mais
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            
            {/* Reposição Hormonal */}
            <div className="service-card group">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C9A227] flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform">
                <Pill className="w-8 h-8 text-white" />
              </div>
              <h3 
                className="text-2xl text-[#2A2A2A] mb-4"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                Reposição Hormonal
              </h3>
              <p className="text-[#555555] mb-6 leading-relaxed">
                Equilíbrio hormonal com critério, segurança e manutenção 
                dentro da faixa fisiológica.
              </p>
              <Link
                to="/reposicao-hormonal"
                data-testid="service-hormonal-link"
                className="text-[#C9A227] font-semibold inline-flex items-center gap-2 group-hover:gap-3 transition-all"
              >
                Saiba mais
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            
            {/* Terapias Endovenosas */}
            <div className="service-card group">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C9A227] flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform">
                <Syringe className="w-8 h-8 text-white" />
              </div>
              <h3 
                className="text-2xl text-[#2A2A2A] mb-4"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                Terapias Endovenosas
              </h3>
              <p className="text-[#555555] mb-6 leading-relaxed">
                Protocolos endovenosos como complemento terapêutico 
                para saúde, energia e recuperação.
              </p>
              <Link
                to="/como-funciona"
                data-testid="service-endovenosas-link"
                className="text-[#C9A227] font-semibold inline-flex items-center gap-2 group-hover:gap-3 transition-all"
              >
                Saiba mais
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Block 7 - Diferenciais Reais */}
      <section data-testid="differentials-section" className="section-padding bg-[#E8DCC8]/50">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <h2 
                className="section-title mb-4"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                O grande diferencial não é a prescrição.
              </h2>
              <p className="text-xl text-[#C9A227] mb-8" style={{ fontFamily: 'var(--font-heading)' }}>
                É o acompanhamento.
              </p>
              
              <ul className="space-y-4">
                {[
                  'Médico que viveu o problema como paciente',
                  'Tratamentos com protocolos individualizados',
                  'Doses mínimas eficazes e seguras',
                  'Acompanhamento próximo e contínuo',
                  'Exames e reavaliações periódicas',
                  'Comunicação direta com o médico',
                  'Foco em resultado sustentável',
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-4">
                    <div className="w-3 h-3 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#C9A227]" />
                    <span className="text-[#2A2A2A]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="card-premium text-center p-6">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C9A227] flex items-center justify-center shadow-lg">
                  <Shield className="w-7 h-7 text-white" />
                </div>
                <p className="text-sm text-[#555555]">Segurança e responsabilidade</p>
              </div>
              <div className="card-premium text-center p-6">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C9A227] flex items-center justify-center shadow-lg">
                  <Heart className="w-7 h-7 text-white" />
                </div>
                <p className="text-sm text-[#555555]">Cuidado humanizado</p>
              </div>
              <div className="card-premium text-center p-6">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C9A227] flex items-center justify-center shadow-lg">
                  <Users className="w-7 h-7 text-white" />
                </div>
                <p className="text-sm text-[#555555]">Tratamento individualizado</p>
              </div>
              <div className="card-premium text-center p-6">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C9A227] flex items-center justify-center shadow-lg">
                  <Clock className="w-7 h-7 text-white" />
                </div>
                <p className="text-sm text-[#555555]">Resultado sustentável</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Block 8 - Segurança e Ética */}
      <section data-testid="ethics-section" className="section-padding bg-[#B68A5A]/15">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 
              className="section-title mb-6"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Segurança e ética médica.
            </h2>
            
            <p className="text-xl text-[#2A2A2A] leading-relaxed mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              Hormônios mudam vidas — para melhor ou para pior.
            </p>
            
            <p className="text-lg text-[#555555] leading-relaxed mb-6">
              A diferença está em como, quando e com quem esse tratamento é feito.
            </p>
            
            <p className="text-lg text-[#555555] leading-relaxed">
              Meu compromisso é conduzir cada caso com{' '}
              <span className="text-[#C9A227] font-semibold">responsabilidade médica</span>,{' '}
              <span className="text-[#C9A227] font-semibold">ciência</span> e{' '}
              <span className="text-[#C9A227] font-semibold">cuidado real</span>.
            </p>
          </div>
        </div>
      </section>

      {/* Block 9 - Prova Social - Antes e Depois */}
      <section data-testid="testimonials-section" className="section-padding">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 
              className="section-title mb-6 text-center"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Histórias reais. Resultados reais.
            </h2>
            
            {/* Antes e Depois */}
            <div className="grid md:grid-cols-2 gap-6 mb-10">
              <div className="relative">
                <div className="aspect-[4/5] rounded-lg overflow-hidden shadow-xl">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_087dcaaa-f596-41d4-9f4c-171b9ddad075/artifacts/85bop39g_ANTES.JPG"
                    alt="Antes do tratamento"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute bottom-4 left-4 bg-[#2A2A2A]/80 text-white px-4 py-2 rounded-md text-sm font-medium">
                  Antes
                </div>
              </div>
              <div className="relative">
                <div className="aspect-[4/5] rounded-lg overflow-hidden shadow-xl">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_087dcaaa-f596-41d4-9f4c-171b9ddad075/artifacts/i3c0vubh_DEPOIS.JPG"
                    alt="Depois do tratamento"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute bottom-4 left-4 bg-gradient-to-r from-[#D4AF37] to-[#C9A227] text-white px-4 py-2 rounded-md text-sm font-medium">
                  Depois
                </div>
              </div>
            </div>
            
            {/* Texto */}
            <div className="text-center">
              <p className="text-lg text-[#2A2A2A] leading-relaxed mb-2 font-medium">
                Esse sou eu e a prova viva do que meu conhecimento e profissionalismo são capazes de fazer.
              </p>
              <p className="text-lg text-[#555555] leading-relaxed mb-8">
                Clique abaixo e conheça outras várias histórias dos meus pacientes, que compartilho no Instagram.
              </p>
              
              <a
                href="https://www.instagram.com/dreduhadas/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="instagram-results-button"
                className="btn-primary inline-flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
                Ver mais histórias no Instagram
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section data-testid="cta-final-section" className="section-padding bg-[#2A2A2A] text-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 
              className="text-3xl md:text-4xl lg:text-5xl text-white mb-6 leading-tight"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Se você quer emagrecer e equilibrar hormônios sem brincar com seu corpo,{' '}
              <span className="text-[#D4AF37]">vamos conversar</span>.
            </h2>
            
            <p className="text-lg text-white/70 mb-10">
              Agende uma avaliação médica e entenda se o meu acompanhamento faz sentido para o seu caso.
            </p>
            
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-final-button"
              className="btn-primary inline-flex items-center gap-2 text-lg"
            >
              <Phone className="w-5 h-5" />
              Agendar avaliação médica
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};
